import { useMemo } from 'react'
import { isEmpty } from 'ramda'

import {
  AllowancesByClasses,
  OrgByCustomersAndFunds,
  OrgByCustomersAndFundsTotals,
} from 'app/dataTypes/reports/data/common/ordersByClasses'

import {
  CompositeTableTemplate,
  renderCellGrayZeros,
} from 'app/common/templates-next/table-template'

import {
  ColumnKey,
  columnKeysLeft,
  createColumnKeysMain,
  createColumnKeysMainHeaders,
  createHeaderCells,
  createDinamicColumnKey,
  createColumnKeysLeftHeaders,
  createHeaderCellsProps,
  createCellsProps,
  createTotalCellProps,
} from './table-columns'

import { headerCell } from './header-cell'

type Props = {
  allowances: AllowancesByClasses
  data: Array<OrgByCustomersAndFunds>
  totals: OrgByCustomersAndFundsTotals
  pending?: boolean
}

export const OrgsGrouppedByCustomersAndFunds = ({
  allowances,
  data,
  totals,
  pending = false,
}: Props) => {
  const tableData = useMemo(() => {
    const hasAllowances = !isEmpty(allowances.countFrom1To4)
    return {
      columnKeysLeft,
      columnKeysLeftHeaders: createColumnKeysLeftHeaders(hasAllowances),
      columnKeysMain: createColumnKeysMain(allowances),
      columnKeysMainHeaders: createColumnKeysMainHeaders(allowances),
      header: createHeaderCells(allowances),
      headerProps: createHeaderCellsProps(allowances),
      data: data.map(({ orgID, orgTitle, countFrom1To4, countFrom5To11 }) => ({
        rowKey: orgID,
        rowData: {
          ...Object.keys(allowances).reduce(
            (acc, key) => {
              ;(allowances[key as keyof AllowancesByClasses] || []).forEach((allowance) => {
                const cKey = createDinamicColumnKey(key, allowance)
                acc[cKey] = {
                  columnKey: cKey,
                  value: String(
                    (key === 'countFrom1To4' ? countFrom1To4 : countFrom5To11).allowances[
                      allowance
                    ],
                  ),
                }
              })
              return acc
            },
            {} as Record<string, { columnKey: string; value: string }>,
          ),

          [ColumnKey.Org]: {
            columnKey: ColumnKey.Org as const,
            value: orgTitle,
          },
          [ColumnKey.ParentsExpensesFrom1To4]: {
            columnKey: ColumnKey.ParentsExpensesFrom1To4 as const,
            value: String(countFrom1To4.parentsExpensesCount),
          },
          [ColumnKey.TotalFrom1To4]: {
            columnKey: ColumnKey.TotalFrom1To4 as const,
            value: String(countFrom1To4.totalCount),
          },
          [ColumnKey.ParentsExpensesFrom5To11]: {
            columnKey: ColumnKey.ParentsExpensesFrom5To11 as const,
            value: String(countFrom5To11.parentsExpensesCount),
          },
          [ColumnKey.TotalFrom5To11]: {
            columnKey: ColumnKey.TotalFrom5To11 as const,
            value: String(countFrom5To11.totalCount),
          },
          [ColumnKey.TotalRow]: {
            columnKey: ColumnKey.TotalFrom5To11 as const,
            value: String(countFrom1To4.totalCount + countFrom5To11.totalCount),
          },
        },
      })),
      dataProps: createCellsProps(allowances),
      total: [
        {
          rowKey: 'total',
          rowData: {
            ...Object.keys(allowances).reduce(
              (acc, key) => {
                ;(allowances[key as keyof AllowancesByClasses] || []).forEach((allowance) => {
                  const cKey = createDinamicColumnKey(key, allowance)
                  acc[cKey] = {
                    columnKey: cKey,
                    value: String(totals[key as keyof typeof totals].allowances[allowance]),
                  }
                })
                return acc
              },
              {} as Record<string, { columnKey: string; value: string }>,
            ),

            [ColumnKey.Org]: {
              columnKey: ColumnKey.Org as const,
              value: 'ИТОГО',
            },
            [ColumnKey.ParentsExpensesFrom1To4]: {
              columnKey: ColumnKey.ParentsExpensesFrom1To4 as const,
              value: String(totals.countFrom1To4.parentsExpensesCount),
            },
            [ColumnKey.TotalFrom1To4]: {
              columnKey: ColumnKey.TotalFrom1To4 as const,
              value: String(totals.countFrom1To4.totalCount),
            },
            [ColumnKey.ParentsExpensesFrom5To11]: {
              columnKey: ColumnKey.ParentsExpensesFrom5To11 as const,
              value: String(totals.countFrom5To11.parentsExpensesCount),
            },
            [ColumnKey.TotalFrom5To11]: {
              columnKey: ColumnKey.TotalFrom5To11 as const,
              value: String(totals.countFrom5To11.totalCount),
            },
            [ColumnKey.TotalRow]: {
              columnKey: ColumnKey.TotalFrom5To11 as const,
              value: String(totals.countFrom1To4.totalCount + totals.countFrom5To11.totalCount),
            },
          },
        },
      ],
      totalProps: createTotalCellProps(allowances),
    }
  }, [allowances, data, totals])

  return (
    <CompositeTableTemplate
      data={tableData}
      loading={pending}
      renderHeaderCell={headerCell}
      renderCell={renderCellGrayZeros}
      renderFooterCell={renderCellGrayZeros}
      borderTop
    />
  )
}
